import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useAuth0, Auth0Provider, LogoutOptions, LogoutUrlOptions } from '@auth0/auth0-react';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import AuthService from '../service/AuthService';
import { api } from '@scoutus/sports-metrics';
import { Config, loadConfig } from '../Config';
import { setOrvalAuthenticationHeader } from '@scoutus/sports-metrics';
import { setAiCoachAuthToken } from '../service/AiCoachService';
import { setBookmarkAnalyticsAuthToken } from '../service/BookmarkAnalyticsService';
import { setClipCreatorAnalyticsAuthToken } from '../service/ClipCreatorAnalyticsService';
import { setAdminAuthToken } from '../service/adminAxiosClient';
import { clearDedupeCache } from '../utils/dedupeAsync';
import { clearPageCache } from '../utils/pageCache';

// let generatedApiPatched = false;

// const patchGeneratedApiAuth = (): void => {
//   if (generatedApiPatched) return;

//   const apiNamespaces = Object.values(api) as Array<Record<string, unknown>>;

//   apiNamespaces.forEach((namespace) => {
//     Object.entries(namespace).forEach(([key, value]) => {
//       if (typeof value !== 'function') return;

//       const original = value as (...args: any[]) => Promise<any>;
//       (namespace as Record<string, unknown>)[key] = (...args: any[]) => {
//         const token = AuthService.getValidToken();

//         if (!token) {
//           return original(...args);
//         }

//         const maybeOptions = args[args.length - 1];
//         const hasAxiosOptions =
//           maybeOptions &&
//           typeof maybeOptions === 'object' &&
//           !Array.isArray(maybeOptions) &&
//           ('headers' in maybeOptions ||
//             'params' in maybeOptions ||
//             'baseURL' in maybeOptions ||
//             'signal' in maybeOptions);

//         if (hasAxiosOptions) {
//           const mergedOptions = {
//             ...maybeOptions,
//             headers: {
//               ...(maybeOptions.headers || {}),
//               Authorization: `Bearer ${token}`,
//             },
//           };
//           return original(...args.slice(0, -1), mergedOptions);
//         }

//         return original(...args, {
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         });
//       };
//     });
//   });

//   generatedApiPatched = true;
// };

// Types
export interface UserProfile {
  id?: string | number;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  profile_image?: string | null;
  [key: string]: any;
}

export interface AdminProfileUpdateInput {
  first_name?: string;
  last_name?: string;
  /** An empty string removes the current picture. */
  profile_image?: string;
  /** PNG or JPEG, up to 5MB. */
  file?: Blob;
}

export interface AuthTokens {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
}

export interface AuthContextType {
  // State
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserProfile | null;
  tokens: AuthTokens;
  permissions: string[];

  // Actions
  login: () => Promise<void>;
  logout: ( logoutParams?: LogoutOptions) => Promise<void>;
  refreshTokens: () => Promise<AuthTokens>;
  updateProfile: (profileData: AdminProfileUpdateInput) => Promise<void>;
  clearAuthState: () => void;

  // Utilities
  getValidToken: () => string | null;
  isTokenExpired: () => boolean;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Internal component that uses Auth0 hooks
const AuthProviderInner: React.FC<AuthProviderProps> = ({ children }) => {
  const { user: auth0User, isAuthenticated: auth0Authenticated, isLoading: auth0Loading, getAccessTokenSilently, logout: auth0Logout, loginWithRedirect } = useAuth0();

  // State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [tokens, setTokens] = useState<AuthTokens>({
    accessToken: null,
    refreshToken: null,
    expiresAt: null,
  });
  const [permissions, setPermissions] = useState<string[]>([]);
  /** Prevents Auth0 user object churn from re-running login and remounting the app. */
  const authBootstrapRef = useRef<'idle' | 'pending' | 'done'>('idle');
  /** Prevents duplicate loginWithRedirect when AuthGuard retries. */
  const loginRedirectInFlightRef = useRef(false);

  
  // // Initialize authentication state on mount
  // useEffect(() => {
  //   initializeAuth();
  // }, []);

  // Handle Auth0 authentication changes
  useEffect(() => {
    if (!auth0Authenticated || !auth0User) return;

    // Already bootstrapped — refresh tokens only. Flipping isLoading would unmount
    // AuthGuard routes and re-fire every page's data loaders (duplicate network calls).
    if (authBootstrapRef.current === 'done') {
      void (async () => {
        try {
          const accessToken = await getAccessTokenSilently();
          setOrvalAuthenticationHeader(accessToken);
          setAiCoachAuthToken(accessToken);
          setBookmarkAnalyticsAuthToken(accessToken);
          setClipCreatorAnalyticsAuthToken(accessToken);
          setAdminAuthToken(accessToken);
        } catch (error) {
          console.error('Failed to refresh Auth0 token:', error);
        }
      })();
      return;
    }

    if (authBootstrapRef.current === 'pending') return;
    authBootstrapRef.current = 'pending';
    void handleAuth0Login()
      .then(() => {
        authBootstrapRef.current = 'done';
        loginRedirectInFlightRef.current = false;
      })
      .catch(() => {
        authBootstrapRef.current = 'idle';
      });
  }, [auth0Authenticated, auth0User, getAccessTokenSilently]);

  // Stay in loading while Auth0 is still resolving the session so guards
  // don't fire loginWithRedirect in parallel with silent authorize.
  useEffect(() => {
    if (auth0Loading) {
      setIsLoading(true);
      return;
    }
    if (!auth0Authenticated) {
      authBootstrapRef.current = 'idle';
      setIsLoading(false);
    }
  }, [auth0Loading, auth0Authenticated]);

  // const initializeAuth = async () => {
  //   try {
  //     setIsLoading(true);

  //     // Check for stored tokens and user data
  //     const storedTokens = getStoredTokens();
  //     const storedUser = getStoredUser();

  //     if (storedTokens.accessToken && storedUser) {
  //       // Validate token hasn't expired
  //       if (!isTokenExpiredAt(storedTokens.expiresAt)) {
  //         setTokens(storedTokens);
  //         setUser(storedUser);
  //         setIsAuthenticated(true);
  //       } else {
  //         // Token expired, try to refresh
  //         await refreshTokens();
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Failed to initialize auth:', error);
  //     clearAuthState();
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleAuth0Login = async () => {
    try {
      setIsLoading(true);

      // Get Auth0 access token
      const accessToken = await getAccessTokenSilently();
      setOrvalAuthenticationHeader(accessToken);
      setAiCoachAuthToken(accessToken);
      setBookmarkAnalyticsAuthToken(accessToken);
      setClipCreatorAnalyticsAuthToken(accessToken);
      setAdminAuthToken(accessToken);

      const decodedAccessToken = jwtDecode(accessToken);
      // const expiresAt = decodedAccessToken.exp ? decodedAccessToken.exp * 1000 : null;
      // const authTokens: AuthTokens = {
      //   accessToken,
      //   refreshToken: null,
      //   expiresAt,
      // };
      // AuthService.storeTokens(authTokens);
      // setTokens(authTokens);

      let auth0Roles: string[] = [];
      auth0Roles = (decodedAccessToken as any)["https://scoutuscollege.com/roles"] || [];
      const auth0Permissions: string[] = (decodedAccessToken as any).permissions || [];
      setPermissions(auth0Permissions);
      const basicUser: UserProfile = {
        ...auth0User,
        roles: auth0Roles,
      };

      // Unblock the app immediately; enrich profile data in the background.
      setUser(basicUser);
      AuthService.storeUser(basicUser);
      setIsAuthenticated(true);
      setIsLoading(false);

      void (async () => {
        try {
          const profileData = (await api.admin.getUserProfileForAdmin() as any)?.data || {};
          const mergedUser: UserProfile = {
            ...basicUser,
            ...profileData,
            roles: auth0Roles,
          };

          setUser(mergedUser);
          AuthService.storeUser(mergedUser);
        } catch (profileError) {
          console.error('Failed to fetch user profile:', profileError);
        }
      })();
    } catch (error) {
      console.error('Auth0 login failed:', error);
      clearAuthState();
    } finally {
      setIsLoading(false);
    }
  };

  const login = useCallback(async () => {
    if (loginRedirectInFlightRef.current) return;
    loginRedirectInFlightRef.current = true;
    try {
      setIsLoading(true);
      await loginWithRedirect();
    } catch (error) {
      console.error('Login failed:', error);
      loginRedirectInFlightRef.current = false;
      setIsLoading(false);
      throw error;
    }
  }, [loginWithRedirect]);

  const logout = async () => {
    try {
      setIsLoading(true);

      // Logout from Auth0
      await auth0Logout({
        // openUrl: false,
        logoutParams: {
          // returnTo: window.location.origin,
          // returnTo: '/',
          returnTo: `${window.location.origin}/`
        },
      });
      clearAuthState();

    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshTokens = async () => {
    try {
      // For Auth0, we just need to get a new access token
      const newAccessToken = await getAccessTokenSilently();
      setOrvalAuthenticationHeader(newAccessToken);
      setAiCoachAuthToken(newAccessToken);
      setBookmarkAnalyticsAuthToken(newAccessToken);
      setClipCreatorAnalyticsAuthToken(newAccessToken);
      setAdminAuthToken(newAccessToken);

      const decodedToken = jwtDecode(newAccessToken);
      const expiresAt = decodedToken.exp ? decodedToken.exp * 1000 : null;

      const updatedTokens: AuthTokens = {
        ...tokens,
        accessToken: newAccessToken,
        expiresAt,
      };

      setTokens(updatedTokens);
      AuthService.storeTokens(updatedTokens);

      return updatedTokens;
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If refresh fails, logout user
      await logout();
      throw error;
    }
  };

  const updateProfile = async (profileData: AdminProfileUpdateInput) => {
    try {
      await api.admin.updateProfile(profileData);

      const response = await api.admin.getUserProfileForAdmin();
      const updatedProfile = response?.data;

      const updatedUser = { ...user, ...updatedProfile };
      setUser(updatedUser);
      AuthService.storeUser(updatedUser);
    } catch (error) {
      console.error('Profile update failed:', error);
      throw error;
    }
  };

  const getValidToken = (): string | null => {
    if (!tokens.accessToken || isTokenExpired()) {
      return null;
    }
    return tokens.accessToken;
  };

  const isTokenExpired = (): boolean => {
    if (!tokens.expiresAt) return true;
    return Date.now() >= tokens.expiresAt;
  };

  

  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  const clearAuthState = () => {
    authBootstrapRef.current = 'idle';
    loginRedirectInFlightRef.current = false;
    AuthService.clearAuthState();
    clearPageCache();
    clearDedupeCache();
    setAdminAuthToken(null);
    setAiCoachAuthToken(null);
    setBookmarkAnalyticsAuthToken(null);
    setClipCreatorAnalyticsAuthToken(null);
    setIsAuthenticated(false);
    setUser(null);
    setPermissions([]);
    setTokens({
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
    });
  };
 


  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    tokens,
    permissions,
    login,
    logout,
    refreshTokens,
    updateProfile,
    clearAuthState,
    getValidToken,
    isTokenExpired,
    hasPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Main AuthProvider that wraps everything with Auth0Provider
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<any>(null);
  const [configLoading, setConfigLoading] = useState(true);

  useEffect(() => {
    const loadAuthConfig = async () => {
      try {
        await loadConfig();
        setConfig({
          domain: Config.AUTH0_DOMAIN,
          clientId: Config.AUTH0_CLIENT_ID,
          audience: Config.AUTH0_AUDIENCE,
          redirectUri: window.location.origin + '/callback',
        });
      } catch (error) {
        console.error('Failed to load Auth0 config:', error);
      } finally {
        setConfigLoading(false);
      }
    };

    loadAuthConfig();
  }, []);

  if (configLoading) {
    return <div>Loading authentication configuration...</div>;
  }

  if (!config) {
    return <div>Failed to load authentication configuration</div>;
  }

  return (
    <Auth0Provider
      domain={config.domain}
      clientId={config.clientId}
      authorizationParams={{
        audience: config.audience,
        redirect_uri: config.redirectUri,
      }}
    >
      <AuthProviderInner>
        {children}
      </AuthProviderInner>
    </Auth0Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
